import {EditorBlock} from 'draft-js';
import {createPortal} from 'react-dom';
import {useRef} from 'react';
import {NextImg} from '@/utils/nextImg';

// eslint-disable-next-line valid-jsdoc
/**
 * Except for ordered & unordered lists, Draft does not natively support nesting a group of blocks
 * in a parent element as is needed for tables.
 * To handle tables, we store the table's size and styling specifications in the first table block
 * and render the entire table DOM structure when we render that first block, setting a data attribute
 * on each <td> element for its position in the table. Then as each subsequent block is rendered we use
 * createPortal from React-Dom to target the correct <td> element in the table to render that block into.
 */
export const Table = props => {
  const {
    block,
    blockProps: {editor},
  } = props;
  // if this is not the first table block, then we target the <td> element in the applicable position
  // to render the current block into.
  if (block.getData().get('tablePosition') && !block.getData().get('tableShape')) {
    const position = block.getData().get('tablePosition');
    const target = editor?.editor.querySelector(`[data-position='${position}']`);
    if (target) {
      return createPortal(<EditorBlock {...props} />, target);
    }
    /**
     * If we get here then the target wasn't in the DOM yet. The reset and paste/insert-related
     * functions use blur() and focus() to trigger a rerender, at which time the DOM will have been
     * updated with the framework of the table structure.
     */
    return null;
  }
  // If we get here we know we are rendering the first block of the table and will render the whole DOM structure of the table
  const data = block.getData();
  const tableKey = data.get('tableKey');
  const tableShape = data.get('tableShape');
  const colgroup = data.get('tableColgroup');

  if (Array.isArray(tableShape)) {
    return (
      <table key={tableKey} style={{borderCollapse: 'collapse', margin: '15px 0', width: '100%'}} id={tableKey}>
        {colgroup && <colgroup dangerouslySetInnerHTML={{__html: colgroup}}></colgroup>}
        <tbody>
          {tableShape.map((row, i) => (
            <tr
              key={i}
            >
              {row.map((cell, j) => {
                if (cell.element === 'th') {
                  return (
                    <th
                      key={j}
                      style={{border: '1px solid #c1c1c1', padding: '6px', backgroundColor: '#e9e9e9', textAlign: 'center'}}
                      colSpan={cell.colspan}
                      rowSpan={cell.rowspan}
                      data-position={`${tableKey}-${i}-${j}`}
                      defaultValue={'test'}
                    >
                      {!!((i === 0) && (j === 0)) && <EditorBlock {...props} />}
                    </th>
                  );
                }
                return (
                  <td
                    key={j}
                    style={{border: '1px solid #c1c1c1', padding: '6px'}}
                    colSpan={cell.colspan}
                    rowSpan={cell.rowspan}
                    data-position={`${tableKey}-${i}-${j}`}
                  >
                    {!!((i === 0) && (j === 0)) && <EditorBlock {...props} />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    return <EditorBlock {...props} />;
  }
};

export const BlockImage = props => {
  const image = useRef(null);
  const {
    block,
    contentState,
  } = props;

  const {content, src, height, width, alt} = block && (block.getEntityAt(0) && contentState.getEntity(block.getEntityAt(0)).getData()) || {};
  const widthImg = width ? typeof width == 'string' && width.includes('%') ? width : `${width}%` : '100%';
  const heightImg = height ? typeof height == 'string' && height.includes('%') ? height : `${height}%` : '100%';

  if (content) {
    const iframeRegex = /<iframe[^>]+src="([^"]+)"[^>]*>/;
    if (typeof content === 'string' && iframeRegex.test(content)) {
      const srcMatch = content.match(/src="([^"]+)"/);
      const widthMatch = content.match(/width="([^"]+)"/);
      const heightMatch = content.match(/height="([^"]+)"/);
      const titleMatch = content.match(/title="([^"]+)"/);
      const urlPattern = /^((http|https):\/\/)/;
      const src = srcMatch ? srcMatch[1] ? urlPattern.test(srcMatch[1]) ? srcMatch[1] : `http://${srcMatch[1]}` : '' : '';
      const width = widthMatch ? +widthMatch[1]>560 ? '100%' : widthMatch[1] : '100%';
      const height = heightMatch ? +heightMatch[1]>315 ? '315px' : heightMatch[1] : '315px';
      const title = titleMatch ? titleMatch[1] : 'iframe content';

      return (
        <iframe
          ref={image}
          src={src}
          title={title}
          width={width}
          height={height}
          frameBorder='0'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerPolicy='strict-origin-when-cross-origin'
          allowFullScreen
        />
      );
    }
    return (
      <iframe
        ref={image}
        src={content?.src}
        title={content?.title || ''}
        width={typeof content?.width === 'string' && content?.width.includes('%') ? content?.width : `${content?.width || 100}%`}
        height={typeof content?.height === 'string' && content?.height.includes('%') ? content?.height : `${content?.height || 100}%`}
        frameBorder='0'
        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        referrerPolicy='strict-origin-when-cross-origin'
        allowFullScreen
      />
    );
  } else {
    return (
      src && <NextImg ref={image} src={src} alt={alt || ''} title={alt || ''} style={{width: '100%', height: '100%'}} width={widthImg} height={heightImg} />
    );
  }
};


const getNumberFromEnglishCounting = data => {
  switch (data) {
    case 'one':
      return 1;
    case 'two':
      return 2;
    case 'three':
      return 3;
    case 'four':
      return 4;
    case 'five':
      return 5;
    case 'six':
      return 6;
    default:
      return null;
  }
};


export function generateHeadingOutline(contentState) {
  const blocks = contentState.getBlocksAsArray();
  const outline = [];

  blocks.forEach(block => {
    const text = block.getText().trim();
    const type = block.getType();

    if (type.startsWith('header')) {
      const level = getNumberFromEnglishCounting(type.substring(type.lastIndexOf('-') + 1));
      const heading = {
        tag: `h${level}`,
        level: level,
        content: text,
      };

      outline.push(heading);
    }
  });

  return outline;
}
